import { Calendar, ChevronDown } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { C } from "../dashboard/_shared.jsx";
import {
  buildPerDefaultLabels,
  buildPerFromPeriods,
} from "./periodPillBig.helpers.js";
import "./style.scss";

export default function PeriodPillBig({
  value,
  onChange,
  custom,
  onCustomChange,
}) {
  const periodsData = useSelector((state) => state.dashboard.periods);
  const periods = useMemo(
    () =>
      (periodsData?.data?.data || []).filter(
        (p) =>
          (!p.city || p.city === "") &&
          (!p.nps_segment || p.nps_segment === "") &&
          (!p.client_type || p.client_type === ""),
      ),
    [periodsData],
  );

  const PER_DEFAULT_LABELS = useMemo(
    () =>
      periods.length
        ? buildPerDefaultLabels(periods)
        : {
            mensal: "",
            trimestral: "",
            semestral: "",
            anual: "",
          },
    [periods],
  );

  const PER = useMemo(
    () =>
      periods.length
        ? buildPerFromPeriods(periods)
        : {
            mensal: null,
            trimestral: null,
            semestral: null,
            anual: null,
          },
    [periods],
  );

  // Agrupa períodos reais por tipo (usando o mesmo mapeamento dos helpers),
  // ordenados por start_date crescente — alimenta os dropdowns.
  const TYPE_KEY_MAP = {
    monthly: "mensal",
    mensal: "mensal",
    quarterly: "trimestral",
    trimestral: "trimestral",
    semiannual: "semestral",
    semestral: "semestral",
    annual: "anual",
    anual: "anual",
  };

  const periodOptions = useMemo(() => {
    const map = { mensal: [], trimestral: [], semestral: [], anual: [] };
    [...periods]
      .sort((a, b) => new Date(a.start_date || 0) - new Date(b.start_date || 0))
      .forEach((p) => {
        const key = TYPE_KEY_MAP[p.type];
        if (key) map[key].push(p.name);
      });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periods]);

  // Deriva o tipo ativo a partir do nome do período selecionado
  const activeTypeKey = useMemo(
    () =>
      Object.keys(periodOptions).find((k) =>
        periodOptions[k].includes(value),
      ) ?? null,
    [value, periodOptions],
  );

  const [showCustom, setShowCustom] = useState(false);
  const [openType, setOpenType] = useState(null);
  const [labels, setLabels] = useState({
    mensal: "",
    trimestral: "",
    semestral: "",
    anual: "",
  });

  // Inicializa labels com o período mais recente de cada tipo
  useEffect(() => {
    if (periods.length) {
      setLabels({
        mensal: periodOptions.mensal.at(-1) || "",
        trimestral: periodOptions.trimestral.at(-1) || "",
        semestral: periodOptions.semestral.at(-1) || "",
        anual: periodOptions.anual.at(-1) || "",
      });
    }
  }, [periodOptions, periods.length]);

  // Sincroniza o label do tipo ativo quando value muda externamente
  useEffect(() => {
    if (activeTypeKey && value) {
      setLabels((prev) => ({ ...prev, [activeTypeKey]: value }));
    }
  }, [activeTypeKey, value]);

  const isCustom = value === "custom";

  return (
    <div className="compPeriodPillBig">
      <Calendar size={15} color={C.text3} />
      <div className="label">Período</div>
      <div className="pill-group">
        {Object.keys(PER).map((k) => {
          const isActive = activeTypeKey === k;
          const isOpen = openType === k;
          return (
            <div key={k} className="type-wrap">
              <button
                onClick={() => {
                  if (isActive) {
                    setOpenType(isOpen ? null : k);
                  } else {
                    // Seleciona o período mais recente desse tipo ao trocar de tipo
                    const defaultPeriod = periodOptions[k].at(-1);
                    if (defaultPeriod) onChange(defaultPeriod);
                    setOpenType(null);
                    setShowCustom(false);
                  }
                }}
                className={`type-btn${isActive ? " is-active" : ""}`}
              >
                {labels[k]}
                <ChevronDown
                  size={13}
                  className="chevron"
                />
              </button>
              {isOpen && (
                <>
                  <div
                    onClick={() => setOpenType(null)}
                    className="backdrop"
                  />
                  <div className="dropdown">
                    {(periodOptions[k] || []).map((opt) => (
                      <button
                        key={opt}
                        onClick={() => {
                          setLabels((l) => ({ ...l, [k]: opt }));
                          onChange(opt);
                          setOpenType(null);
                          setShowCustom(false);
                        }}
                        className={`dropdown-btn${labels[k] === opt ? " is-selected" : ""}`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
